import utils from '@/utils'
import * as memberService from '@/service/member'
import { usePlatformStore } from '@/store-pinia/platform'

import { useRecaptcha } from '@/composition/useRecaptcha'
import { ReCaptchaAction } from '@/view-models/recaptcha'
import { useApiClient } from '@/composables/useApiClient'

const oneYear = 60 * 60 * 24 * 365

export const state = () => ({
  user: null,
  isUserInfoLoading: false,
})

export const getters = {
  getUser: state => state.user,
  getIsUserInfoLoading: state => state.isUserInfoLoading,
}

export const actions = {
  resetUserInfo({ commit }) {
    if (import.meta.server) {
      return
    }
    try {
      const nuxtApp = useNuxtApp()
      nuxtApp.$cookiz.remove('FNaccount')
      nuxtApp.$cookiz.remove('FNcurrency')
      nuxtApp.$auth.setUser(false)
      nuxtApp.$auth.$storage.removeUniversal('user')
    } catch (error) {
      // catch plugin error in server side
    }
    commit('SET_USER', null)
  },
  getUserInfo({ dispatch, commit, state }) {
    commit('SET_USER_INFO_LOADING', true)
    return useApiClient()
      .$get('/member')
      .then(res => {
        if (res.code === 0) {
          const userData = res.data
          useNuxtApp().$auth.setUser(userData)
          useNuxtApp().$auth.$storage.setUniversal('user', userData)
          commit('SET_USER', res.data)
          return res.data
        }
      })
      .catch(err => {
        dispatch('resetUserInfo')
        return err
      })
      .finally(() => {
        commit('SET_USER_INFO_LOADING', false)
      })
  },
  updateUserInfo({ commit }, data) {
    // data { birthday, currency, email, gender, nickname, phone }
    return useApiClient()
      .$post('/member', data)
      .then(res => {
        return res
      })
      .catch(error => {
        console.error(error)
      })
  },
  cathayLogin({ commit }, queryData) {
    const data = Object.assign(
      {
        os: 'web',
        osVersion: navigator.userAgent,
        appVersion: '1.0',
        deviceId: utils.UUID,
      },
      queryData
    )
    return useApiClient()
      .$post('/login/cathay', data)
      .then(res => {
        if (res.code === 0) {
          useNuxtApp().$auth.$storage.setUniversal('user', res.data)
          commit('SET_USER', res.data)
        }
        return res
      })
      .catch(err => {
        return err
      })
  },
  async login({ commit }, { username, password, anonymous = false }) {
    const { execute } = useRecaptcha()
    const recaptchaToken = await execute(ReCaptchaAction.LOGIN)

    const data = {
      username: anonymous ? void 0 : username,
      password: anonymous ? void 0 : password,
      os: 'web',
      osVersion: navigator.userAgent,
      appVersion: '1.0',
      deviceId: utils.UUID,
      verification_token: recaptchaToken,
    }

    const loginStrategyName = anonymous ? 'anonymous' : 'local'
    const responseData = await useNuxtApp().$auth.loginWith(loginStrategyName, data)

    if (responseData.code !== 0) return

    const userData = responseData.data
    const platformStore = usePlatformStore(useNuxtApp().$pinia)
    if (anonymous) {
      platformStore.isAnonymousUser = true
    } else {
      useNuxtApp().$auth.setUser(userData)
      useNuxtApp().$auth.$storage.setUniversal('user', userData)
      commit('SET_USER', userData)
      platformStore.isAnonymousUser = false
    }
    return responseData
  },
  getFbAccessToken({ commit }, { code, redirectUri }) {
    const config = useRuntimeConfig()
    const url = `https://graph.facebook.com/oauth/access_token?client_id=${config.public.FB_APP_ID}&client_secret=${config.FB_APP_SECRET}&code=${code}&redirect_uri=${redirectUri}`
    return fetch(url)
      .then(res => res.json())
      .then(json => {
        if (json?.error?.message) {
          throw json.error
        } else {
          return json.access_token
        }
      })
      .catch(err => {
        console.error('error is: ', err.message)
        throw err
      })
  },
  getFbProfile({ commit }, accessToken) {
    const url = `https://graph.facebook.com/v22.0/me?access_token=${accessToken}&fields=id%2Cname%2Cemail%2Cbirthday%2Cgender&method=get&pretty=0&sdk=joey&suppress_http_code=1`
    return fetch(url)
      .then(res => res.json())
      .then(json => {
        if (json?.error?.message) {
          throw json.error
        } else {
          return json
        }
      })
      .catch(err => {
        console.error('error is: ', err.message)
        throw err
      })
  },
  fbLogin({ commit }, { fbToken, id }) {
    const fbLoginData = {
      fbToken: fbToken,
      fbId: id,
      os: 'web',
      osVersion: navigator.userAgent,
      appVersion: '1.0',
      deviceId: utils.UUID,
    }
    return new Promise((resolve, reject) => {
      useApiClient()
        .$post('/fblogin', fbLoginData)
        .then(res => {
          resolve(res)
        })
        .catch(err => {
          reject(err)
        })
    })
  },
  logout({ dispatch, commit }) {
    return new Promise((resolve, reject) => {
      useApiClient()
        .$post('/logout')
        .then(res => {
          dispatch('resetUserInfo')
          resolve(res)
        })
        .catch(err => {
          reject(err)
        })
    })
  },
  async register(context, { data, isFromFB = false }) {
    const { execute } = useRecaptcha()
    const regionId =
      context.rootGetters['region/region'].id ||
      context.rootGetters['branch/branch'].region_id ||
      context.rootGetters['product/productInfo'].region_id

    const defaultData = {
      os: 'web',
      osVersion: navigator.userAgent,
      appVersion: '1.0',
      deviceId: utils.UUID,
      fromFacebook: isFromFB || false,
    }
    const recaptchaToken = await execute(ReCaptchaAction.REGISTER)
    const registerData = Object.assign(defaultData, data, { verification_token: recaptchaToken })

    const res = await useApiClient().$post('/register', registerData)

    if (res.code !== 0) {
      return res
    }

    let userInfo = res.data
    if (regionId) {
      await memberService.updateMemberRegion(useApiClient(), regionId)

      userInfo = {
        ...userInfo,
        region_id: regionId,
      }
    }

    useNuxtApp().$auth.$storage.setUniversal('user', userInfo)
    context.commit('SET_USER', userInfo)
    return res
  },
  async updateMemberRegion({ commit }, { regionId, userInfo }) {
    await memberService.updateMemberRegion(useApiClient(), regionId)

    const newUserInfo = {
      ...userInfo,
      region_id: regionId,
    }

    commit('UPDATE_USER_REGION_ID', regionId)
    useNuxtApp().$auth.$storage.setUniversal('user', newUserInfo)
  },
  async forgotPassword({ commit }, data) {
    const { execute } = useRecaptcha()
    const recaptchaToken = await execute(ReCaptchaAction.PASSWORD_RECOVER)

    const res = useApiClient().$post('/password/recover', {
      ...data,
      verification_token: recaptchaToken,
    })
    return res
  },
  resetPassword({ commit }, data) {
    return new Promise((resolve, reject) => {
      useApiClient()
        .$post('/password/reset', data)
        .then(res => {
          resolve(res)
        })
        .catch(err => {
          reject(err)
        })
    })
  },
  setCookie({ commit }, data) {
    for (const k in data) {
      useNuxtApp().$cookiz.set(k, data[k], { path: '/', maxAge: oneYear })
    }
  },
  async sendSMSCode({ commit }, data) {
    const { execute } = useRecaptcha()
    const recaptchaToken = await execute(ReCaptchaAction.SMS_REQUEST)

    const res = await useApiClient().$post('/member/smsRequest', { ...data, verification_token: recaptchaToken })
    return res
  },
  verifySMSCode({ commit }, data) {
    const apiUrl = '/member/smsVerify'
    return useApiClient()
      .$post(apiUrl, data)
      .then(res => {
        return res
      })
      .catch(error => {
        return error
      })
  },
  getCanAccountBeDeleted({ commit }, token) {
    const apiUrl = `/member/deletion/${token}`
    return useApiClient()
      .$get(apiUrl)
      .then(res => {
        return res
      })
      .catch(err => {
        console.error('error is: ', err.message)
        return err
      })
  },
  deleteAccount({ commit }, token) {
    const apiUrl = `/member/deletion/${token}`
    return useApiClient()
      .$post(apiUrl)
      .then(res => {
        return res
      })
      .catch(err => {
        console.error('error is: ', err.message)
        throw err
      })
  },
}

export const mutations = {
  SET_USER(state, user) {
    state.user = user
  },
  SET_USER_INFO_LOADING(state, isLoading) {
    state.isUserInfoLoading = isLoading
  },
  UPDATE_USER_REGION_ID(state, regionId) {
    state.user = {
      ...state.user,
      region_id: regionId,
    }
  },
  UPDATE_USER_IS_ELITE(state, isElite) {
    state.user = {
      ...state.user,
      is_elite: isElite,
    }
  },
  UPDATE_ANONYMOUS_DATA(state, flag) {
    state.isAnonymousUser = !!flag
  },
}
